'use client';

import type { MutableRefObject } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { isNumber } from '_internal/lang/isNumber';
import { useAnimateItemContext } from './AnimateItemContext';
import { useAnimateMutationContext } from './AnimateMutationProvider';

type Props = {
  y?: number;
  x?: number;
  debugId?: string;
};

export function useAnimateItem(
  ref: MutableRefObject<any>,
  { y: yProp = 0, x: xProp = 0 }: Props
) {
  const { duration } = useAnimateMutationContext();
  const {
    animated,
    type,
    isFullyMeasured,
    startY: startYDuringMount,
    exitY: exitYDuringUnmount,
    startX: startXDuringMount,
    exitX: exitXDuringUnmount,
  } = useAnimateItemContext();

  const y = isNumber(exitYDuringUnmount) ? exitYDuringUnmount : yProp;
  const x = isNumber(exitXDuringUnmount) ? exitXDuringUnmount : xProp;

  const [animateMount] = useState(type === 'mount-mutation');

  const isExiting = type === 'exit-mutation';

  const animationRef = useRef(null);

  useLayoutEffect(() => {
    if (animationRef.current) {
      // @ts-expect-error FIXME(strictNullChecks)
      animationRef.current.cancel();
    }

    if (isExiting) {
      // unmount animation
      animationRef.current = ref.current.animate(
        {
          pointerEvents: 'none',
          opacity: 0,
          scale: [1, 0.9],
        },
        {
          duration: duration / 2,
          fill: 'both',
          easing: 'ease-out',
        }
      );
    } else if (animateMount) {
      // mount animation
      animationRef.current = ref.current.animate(
        {
          // we should properly interrupt the opacity here
          opacity: [0, 1],
          scale: [0.9, 1],
        },
        {
          duration,
          easing: 'ease-in-out',
        }
      );
    }
  }, [isExiting, ref, duration, animateMount]);

  // move animation
  const startYRef = useRef(
    isNumber(startYDuringMount) ? startYDuringMount : null
  );
  const startXRef = useRef(
    isNumber(startXDuringMount) ? startXDuringMount : null
  );

  useLayoutEffect(() => {
    if (!animated) {
      // can we do this with regular inline styles instead?
      // for SSR we'd need to
      if (ref.current) {
        ref.current.style.top = `${y}px`;
        ref.current.style.left = `${x}px`;
      }
      startYRef.current = y;
      startXRef.current = x;
    }
  }, [animated, ref, y, x]);

  const moveAnimationRef = useRef(null);

  useLayoutEffect(() => {
    // only animate the position if we are fully measured, otherwise
    // wait for the measurement to finish and then update the position.
    if (!animated || !isFullyMeasured || isExiting) {
      return;
    }

    let startY = startYRef.current;
    let startX = startXRef.current;

    if (startY === null) {
      ref.current.style.top = `${y}px`;
      ref.current.style.left = `${x}px`;
      startYRef.current = y;
      startXRef.current = x;
      return;
    }

    // support interrupting existing animations
    if (moveAnimationRef.current) {
      const node = ref.current;
      const currentTransform = new WebKitCSSMatrix(
        window.getComputedStyle(node).transform
      );
      startY = node.offsetTop + currentTransform.m42;
      startX = node.offsetLeft + currentTransform.m41;
      // @ts-expect-error FIXME(strictNullChecks)
      moveAnimationRef.current.cancel();
    }

    ref.current.style.zIndex = 1;
    ref.current.style.top = `${y}px`;
    ref.current.style.left = `${x}px`;

    // @ts-expect-error FIXME(strictNullChecks)
    const deltaY = startY - y;
    // @ts-expect-error FIXME(strictNullChecks)
    const deltaX = startX - x;

    moveAnimationRef.current = ref.current.animate(
      {
        transform: [
          `translateY(${deltaY}px) translateX(${deltaX}px) `,
          'translateY(0) translateX(0)',
        ],
      },
      {
        duration,
        easing: 'ease',
      }
    );

    const el = ref.current;

    // @ts-expect-error FIXME(strictNullChecks)
    moveAnimationRef.current.onfinish = () => {
      moveAnimationRef.current = null;
      if (el) {
        el.style.zIndex = null;
      }
    };
  }, [animated, isExiting, isFullyMeasured, ref, duration, y, x]);

  return {
    animated,
  };
}
